import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button/constants';
import {
  baseButtonStyles,
  createDisabledStyles,
  createSizeStyles,
  createVariantStyles,
  DISABLED_STYLE,
} from 'ui/button/Styled';
import { spacing } from 'ui/theme';
import { ANGULAR_DATA_TEST_TAG, DATA_TEST_TAG } from './constants';

export type LinkButtonProps = {
  children: ReactNode;
  /** the route to which pressing this component will link  */
  to: string;
  /** if the page being routed to is angular, this component will return an anchor element. if it is a react route, it will use a react-router Link component.  */
  isAngular?: boolean;
  /** An override to disable the button */
  isDisabled?: boolean;
  /** The size variant of the button */
  size?: BUTTON_SIZES;
  /** The type variant of the button */
  variant?: BUTTON_VARIANTS;
  /** link rel prop */
  rel?: string;
  /** link target prop */
  target?: string;
  /** data-test attribute for testing */
  'data-test'?: string;
};

export type LinkProps = {
  /** The size variant of the button */
  size: BUTTON_SIZES;
  /** The type variant of the button */
  variant: BUTTON_VARIANTS;
};

const styles = css`
  ${baseButtonStyles}
  box-sizing: border-box;
  text-decoration: none;
`;

export const StyledButtonLabel = styled.span`
  display: inline-block;
  margin-left: ${spacing.half};
`;

export const DISABLED_STYLES_FOR_LINK_BUTTON = css`
  ${DISABLED_STYLE};
  pointer-events: none;
`;

const createCommonLinkStyles = ({ size, variant }: LinkProps) => css`
  ${styles}
  ${createSizeStyles(variant, size)};
  ${createVariantStyles(variant)};

  &[aria-disabled='true'] {
    ${createDisabledStyles(true, variant)};
  }
`;

export const StyledLink = styled(Link)<LinkProps>`
  ${({ size, variant }) => createCommonLinkStyles({ size, variant })};
`;

export const StyledAnchor = styled.a<LinkProps>`
  ${({ size, variant }) => createCommonLinkStyles({ size, variant })};
`;

export const LinkButton = ({
  to,
  isAngular,
  isDisabled,
  children,
  size = BUTTON_SIZES.MEDIUM,
  variant = BUTTON_VARIANTS.PRIMARY,
  ...props
}: LinkButtonProps) => {
  if (isAngular) {
    return (
      <StyledAnchor
        aria-disabled={isDisabled}
        data-test={props['data-test'] || ANGULAR_DATA_TEST_TAG}
        href={to}
        size={size}
        variant={variant}
        {...props}
      >
        {children}
      </StyledAnchor>
    );
  }

  return (
    <StyledLink
      aria-disabled={isDisabled}
      data-test={props['data-test'] || DATA_TEST_TAG}
      to={to}
      size={size}
      variant={variant}
      {...props}
    >
      {children}
    </StyledLink>
  );
};
